import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FormikProvider, useFormik } from 'formik';
import { Button, Input, ModalFactory } from 'components';
import Form from 'screens/Error/Form';
import { selectIsLoadingAPIRequest } from 'store/ApiClient';
import { LaborReview as LaborReviewActionTypes, updateLaborRequest } from 'store/LaborReview';
import { IStore } from 'store/types';
import { ILabor } from 'types';
import { OverrideReasonCallout } from './OverrideReasonCallout';
import { buildShiftValidationSchema, IShiftFormValues, newFormSchedule, ShiftFieldArray, toLaborSchedule } from './ShiftForm';
import { ModalButtonRow, ModalSubtitle, RequiredField, ShiftFormLayout } from './styles';
import { formatBusinessDate } from './utils';

interface IAddShiftModalProps {
  siteId: string;
  date: string;
  locationId: string;
  timezone: string;
  businessDayStart: string;
  employeeId: string;
  employeeName?: string;
  // Absent when Legion sent no labor and no one has added any yet.
  labor?: ILabor;
  jobTitles: string[];
  onClose: () => void;
}

const validationSchema = buildShiftValidationSchema('Please provide a reason for adding this labor');

export const AddShiftModal: React.FC<IAddShiftModalProps> = ({ siteId, date, locationId, timezone, businessDayStart, employeeId, employeeName, labor, jobTitles, onClose }) => {
  const dispatch = useDispatch();
  const isUpdating = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, LaborReviewActionTypes.UPDATE_LABOR_REQUEST));

  const formik = useFormik<IShiftFormValues>({
    initialValues: {
      schedules: [newFormSchedule()],
      reason: '',
    },
    onSubmit: (formValues) => {
      const schedules = formValues.schedules.map((schedule) => toLaborSchedule({ schedule, date, timezone, businessDayStart }));

      dispatch(updateLaborRequest(siteId, date, locationId, employeeId, { schedules, version: labor?.version, reason: formValues.reason.trim() }, onClose));
    },
    validateOnBlur: true,
    validationSchema,
  });

  const { dirty, handleSubmit, setFieldValue, values } = formik;

  const reasonError = formik.touched.reason && formik.errors.reason ? formik.errors.reason : '';

  return (
    <ModalFactory title={`Add labor — ${employeeName || `#${employeeId}`}`} onClose={onClose} isActive maxWidth={566} closeOnOutSideClick={false}>
      <ModalSubtitle>
        {labor?.overridden ? 'All labor was removed' : 'Legion sent no labor'} for this employee on {formatBusinessDate(date)}.
      </ModalSubtitle>

      <OverrideReasonCallout overridden={labor?.overridden} label="This labor already has a manual override" />

      <FormikProvider value={formik}>
        <Form onSubmit={handleSubmit} dataCy="add-shift-form">
          <ShiftFormLayout>
            <ShiftFieldArray schedules={values.schedules} jobTitles={jobTitles} setFieldValue={setFieldValue} hideSingleShiftHeader />

            <RequiredField label="Reason for adding" message={reasonError} hasError={Boolean(reasonError)}>
              <Input placeholder="Add a reason for adding a labor" data-cy="add-reason" {...formik.getFieldProps('reason')} />
            </RequiredField>
          </ShiftFormLayout>

          <ModalButtonRow>
            <Button appearance="white" label="Cancel" onClick={onClose} disabled={isUpdating} />
            <Button type="submit" label="Add labor" isProcessing={isUpdating} disabled={isUpdating || !dirty} />
          </ModalButtonRow>
        </Form>
      </FormikProvider>
    </ModalFactory>
  );
};
